import Text from 'components/Text/Text';
import React, { useContext, useEffect, useCallback } from 'react';
import { Form } from 'antd';
import NameField from './components/NameField';
import EmailField from './components/EmailField';
import { BtnBlock } from './components/BtnBlock';
import EmailBodyTemplateField from './components/EmailBodyTemplateField';
import ClientTypeSelect from './components/ClientTypeSelect';
import BillTemplateBlock from 'components/BillTemplateBlock/BillTemplateBlock';
import PriceTypeField from './components/PriceTypeField';
import AdsField from './components/AdsField';
import { TipsBlock } from 'components/TipsBlock/TipsBlock';
import DefaultLanguageSelect from './components/DefaultLanguageSelect';
import LanguagesMultipleSelect from './components/LanguagesMultipleSelect';
import { MixpanelTokenField } from './components/MixpanelTokenField';
import { StopListSyncIntervalField } from './components/StopListSyncIntervalField';
import { CreateCustomerContext, ICreateCustomerContext } from '../../../../createCustomerState/createCustomerContext';
import { preventDefaultForEnterKey } from 'utils/helpers';
import style from './InputFields.module.scss';

export const InputFields: React.FC = () => {
	const {
		changeTypeOfModalWindow,
		setDataForSubmit,
		changeName,
		changeEmail,
		changeEmailBodyTemplate,
		setClientType,
		setTips0,
		setTips1,
		setTips2,
		setTips3,
		setDefaultTips,
		changeBillTemplateFileData,
		changeBillAdvertisingImageData,
		changeBillAdvertisingLink,
		changePriceType,
		setAds,
		setMixpanelToken,
		setStopListSyncIntervalMinutes,
		setCustomerDefaultLanguage,
		setCustomerLanguages,
		setFetchedLanguages,
		fileId,
		name,
		email,
		emailBodyTemplate,
		clientType,
		billTemplateFileData,
		billAdvertisingImageData,
		billAdvertisingLink,
		priceType,
		tips0,
		tips1,
		tips2,
		tips3,
		defaultTips,
		ads,
		mixpanelToken,
		stopListSyncIntervalMinutes,
		IDSelectedCustomerDefaultLanguage,
		IDsSelectedCustomerLanguages,
	} = useContext<ICreateCustomerContext>(CreateCustomerContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({
			name,
			email,
			emailBodyTemplate,
			clientType,
			billAdvertisingLink,
			priceType,
			tips0,
			tips1,
			tips2,
			tips3,
			defaultTips,
			ads,
			mixpanelToken,
			stopListSyncIntervalMinutes,
			IDSelectedCustomerDefaultLanguage,
			IDsSelectedCustomerLanguages,
		});
	}, [
		name,
		email,
		emailBodyTemplate,
		clientType,
		billAdvertisingLink,
		priceType,
		tips0,
		tips1,
		tips2,
		tips3,
		defaultTips,
		ads,
		mixpanelToken,
		stopListSyncIntervalMinutes,
		IDSelectedCustomerDefaultLanguage,
		IDsSelectedCustomerLanguages,
	]);

	const submit = async () => {
		setDataForSubmit({
			fileId,
			name,
			email,
			emailBodyTemplate,
			clientType: clientType || 1,
			billTemplateId: billTemplateFileData.fileId,
			billAdvertisingImage: billAdvertisingImageData.fileId,
			billAdvertisingLink,
			priceType,
			defaultTips: [
				{ value: tips0, isDefault: defaultTips === '0' },
				{ value: tips1, isDefault: defaultTips === '1' },
				{ value: tips2, isDefault: defaultTips === '2' },
				{ value: tips3, isDefault: defaultTips === '3' },
			],
			ads: Number(ads),
			mixpanelToken: mixpanelToken || null,
			stopListSyncIntervalMinutes: stopListSyncIntervalMinutes || 0,
			defaultLanguageId: IDSelectedCustomerDefaultLanguage || '',
			availableLanguageIds: IDsSelectedCustomerLanguages,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	const handleImageFileSizeExceeded = useCallback(() => changeTypeOfModalWindow('imageFileSizeExceeded'), []);
	const handleHtmlFileSizeExceeded = useCallback(() => changeTypeOfModalWindow('htmlFileSizeExceeded'), []);

	const handleDeleteBillAdvertisingImage = useCallback(
		() =>
			changeBillAdvertisingImageData({
				fileId: '',
				fileHash: null,
				fileName: null,
			}),
		[],
	);

	return (
		<div className={style.container} data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<div className={style.row}>
						<NameField changeName={changeName} />
						<EmailField changeEmail={changeEmail} />
					</div>
					<div className={style.row}>
						<EmailBodyTemplateField changeEmailBodyTemplate={changeEmailBodyTemplate} />
						<ClientTypeSelect setClientType={setClientType} />
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<BillTemplateBlock
						changeBillTemplateFileData={changeBillTemplateFileData}
						changeBillAdvertisingImageData={changeBillAdvertisingImageData}
						changeBillAdvertisingLink={changeBillAdvertisingLink}
						onImageFileSizeExceeded={handleImageFileSizeExceeded}
						onHtmlFileSizeExceeded={handleHtmlFileSizeExceeded}
						onDeleteBillAdvertisingImage={handleDeleteBillAdvertisingImage}
						billTemplateFileData={billTemplateFileData}
						billAdvertisingImageData={billAdvertisingImageData}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="priceTypeHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<PriceTypeField changePriceType={changePriceType} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="tipsHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<TipsBlock
						setTips0={setTips0}
						setTips1={setTips1}
						setTips2={setTips2}
						setTips3={setTips3}
						setDefaultTips={setDefaultTips}
						defaultTips={defaultTips}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="adsHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<AdsField setAds={setAds} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="languagesHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<div className={style.row}>
						<DefaultLanguageSelect
							setCustomerDefaultLanguage={setCustomerDefaultLanguage}
							setFetchedLanguages={setFetchedLanguages}
						/>
						<LanguagesMultipleSelect
							IDSelectedCustomerDefaultLanguage={IDSelectedCustomerDefaultLanguage}
							IDsSelectedCustomerLanguages={IDsSelectedCustomerLanguages}
							setCustomerLanguages={setCustomerLanguages}
						/>
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="enableStopListHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<StopListSyncIntervalField setStopListSyncIntervalMinutes={setStopListSyncIntervalMinutes} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mixpanelHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<MixpanelTokenField setMixpanelToken={setMixpanelToken} />
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};
